import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NatureControle } from 'src/app/data/declaration/models/nature-controle.model';
import { SessionContext } from 'src/app/core/services/config/app.settings';
import { ReferentielService } from 'src/app/data/declaration/services/referentiel/referentiel.service';
import { Animateur } from 'src/app/data/commission/models/animateur.model';
import { forkJoin } from 'rxjs';
import { AnimateursService } from 'src/app/data/commission/services/animateurs/animateurs.service';
import { ManquementsService } from 'src/app/data/declaration/services/manquements/manquements.service';
import * as moment from 'moment';
import { ManquementResultat } from 'src/app/data/declaration/models/ManquementResultat.model';
import { PaginateResponseStream } from 'src/app/shared/services/pagination/pagination.service';
import { StatutManquement } from 'src/app/data/declaration/models/statut-manquement.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EditManquementComponent } from './modals/edit-manquement/edit-manquement.component';
import { TelechargerDocumentComponent } from '../../../main/components/telecharger-document/telecharger-document.component';
import { CategorieEnum } from 'src/app/data/edition/models/enums/categorie.enum.model';
import { LoaderService } from '../../../../../core/services/loader/loader.service';

@Component({
    selector: 'app-search-manquements',
    templateUrl: './search-manquements.component.html',
    styleUrls: ['./search-manquements.component.scss'],
    standalone: false
})
export class SearchManquementsComponent implements OnInit {
    private idOrganisme: number;
    public pagination: PaginateResponseStream<ManquementResultat[]>;
    public refAgents: Animateur[];
    public refNatures: NatureControle[];
    public refStatuts: StatutManquement[];
    public formSearchManquements: UntypedFormGroup;

    isAdvancedCollapsed = true;
    showAdvancedFilters = false;

    get nature() {
        return this.formSearchManquements.get('nature');
    }
    get statuts() {
        return this.formSearchManquements.get('statuts');
    }
    get debut() {
        return this.formSearchManquements.get('debut');
    }
    get fin() {
        return this.formSearchManquements.get('fin');
    }
    get agents() {
        return this.formSearchManquements.get('agents');
    }
    get numeroManquement() {
        return this.formSearchManquements.get('numeroManquement');
    }
    get numeroControle() {
        return this.formSearchManquements.get('numeroControle');
    }
    get numeroCVI() {
        return this.formSearchManquements.get('numeroCVI');
    }

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly referentielService: ReferentielService,
        private readonly animateursService: AnimateursService,
        private readonly loaderService: LoaderService,
        private readonly manquementsService: ManquementsService,
        private readonly modalService: NgbModal
    ) {}

    ngOnInit() {
        // Obtenir l'identifiant de l'organisme
        this.idOrganisme = SessionContext.get('idOrganisme');

        // Initialiser le formulaire de recherche
        this.formSearchManquements = this.fb.group({
            nature: [undefined, Validators.required],
            statuts: undefined,
            debut: undefined,
            fin: undefined,
            agents: undefined,
            numeroManquement: undefined,
            numeroControle: undefined,
            numeroCVI: undefined
        });

        // Charger les données de référence pour les listes déroulantes
        this.loadData();
    }

    /**
     * Charge la liste des natures et statuts de contrôle ainsi que
     * la liste des animateurs
     */
    private loadData(): void {
        forkJoin(this.referentielService.getReferentiel(), this.animateursService.getAnimateurs(this.idOrganisme))
            .pipe(
                // Cacher le loader
                this.loaderService.operator()
            )
            .subscribe(
                ([referentiel, animateurs]) =>
                    // Stocker les résultats dans les variables de référence
                    ([this.refNatures, this.refStatuts, this.refAgents] = [referentiel.naturesControle, referentiel.statutsManquement, animateurs])
            );
    }

    /** Valide le formulaire et lance la recherche des manquements */
    public onSubmit(): void {
        // Vérifier si le formulaire est valide
        if (this.formSearchManquements.valid) {
            this.manquementsService
                .getFilteredManquements(
                    this.idOrganisme,
                    this.nature.value.id,
                    this.statuts.value ? this.statuts.value.map(statut => statut.id) : null,
                    this.getMomentDate(this.debut.value),
                    this.getMomentDate(this.fin.value),
                    this.agents.value ? this.agents.value.map(agent => agent.id) : null,
                    this.numeroManquement.value,
                    this.numeroControle.value,
                    this.numeroCVI.value
                )
                .subscribe(manquementsPagines => (this.pagination = manquementsPagines));
        }
    }

    public onEdit(manquement: ManquementResultat): void {
        const modal = this.modalService.open(EditManquementComponent, { backdrop: 'static', windowClass: 'app-modal-1000px' });
        modal.componentInstance.manquement = manquement;

        modal.result.then(
            () => {},
            () => {}
        );
    }

    public telechargerDocument(manquement: ManquementResultat) {
        const modalRef = this.modalService.open(TelechargerDocumentComponent, { size: 'lg', backdrop: 'static' });
        modalRef.componentInstance.manquement = manquement;
        modalRef.componentInstance.categorie = CategorieEnum.MANQUEMENT;
        modalRef.result.then(
            () => {},
            error => {
                throw error;
            }
        );
    }

    /**
     * Convertit une date en provenance d'un datepicker vers un objet de type momentJS
     * @param date La date à convertir
     * @see moment.Moment
     * @returns moment.Moment ou null
     */
    private getMomentDate(date: { year: number; month: number; day: number }): moment.Moment {
        return date ? moment([date.year, date.month - 1, date.day, 0, 0, 0]) : null;
    }
   

    getLibelles(items: any[], property: string): string {
        if (!items?.length) {
            return '';
        }

        return items
            .map(item => item?.[property])
            .filter(Boolean)
            .join(', ');
    }

    hasActiveFiltersManquements(): boolean {
        const formValue = this.formSearchManquements?.value;

        return !!(
            formValue?.nature ||
            formValue?.statuts?.length ||
            formValue?.debut ||
            formValue?.fin ||
            formValue?.agents?.length ||
            formValue?.numeroManquement ||
            formValue?.numeroControle ||
            formValue?.numeroCVI
        );
    }

    clearManquementField(field: string): void {
        const control = this.formSearchManquements.get(field);

        if (!control) {
            return;
        }

        if (Array.isArray(control.value)) {
            control.setValue([]);
        } else {
            control.setValue(null);
        }

        control.markAsDirty();
        control.markAsTouched();
        }

    resetManquementsForm(): void {
        this.formSearchManquements.reset({
            nature: null,
            statuts: [],
            debut: null,
            fin: null,
            agents: [],
            numeroManquement: null,
            numeroControle: null,
            numeroCVI: null
        });
    }
}
